import { FraudSeverity } from '../../../../../services/verificationClient';

export type FraudSignalKey =
  | 'dob_plausibility'
  | 'expiry_status'
  | 'extracted_id_looks_synthetic'
  | 'surname_consistent'
  | 'id_number_match';

const SIGNAL_SEVERITY: Record<FraudSignalKey, Record<string, FraudSeverity>> = {
  dob_plausibility: {
    future: 'critical',
    implausible_old: 'critical',
    implausible_young: 'critical',
    plausible: 'ok',
  },
  expiry_status: {
    long_expired: 'critical',
    expired: 'medium',
    expiring_soon: 'low',
    valid: 'ok',
    'n/a': 'ok',
  },
  extracted_id_looks_synthetic: { true: 'critical', false: 'ok' },
  surname_consistent: { mismatch: 'medium', approximate: 'low', exact: 'ok' },
  id_number_match: { mismatch: 'high', match: 'ok' },
};

export const SIGNAL_SHORT_LABEL: Record<FraudSignalKey, string> = {
  dob_plausibility: 'DOB',
  expiry_status: 'expiry',
  extracted_id_looks_synthetic: 'synthetic',
  surname_consistent: 'surname',
  id_number_match: 'ID number',
};

export const FRAUD_SIGNAL_DETAILS: Array<{ key: FraudSignalKey; label: string }> = [
  { key: 'dob_plausibility', label: 'DOB plausibility' },
  { key: 'expiry_status', label: 'Expiry status' },
  { key: 'extracted_id_looks_synthetic', label: 'Synthetic shape' },
  { key: 'surname_consistent', label: 'Surname vs employee' },
  { key: 'id_number_match', label: 'ID number match' },
];

function isFraudSignalKey(key: string): key is FraudSignalKey {
  return Object.prototype.hasOwnProperty.call(SIGNAL_SEVERITY, key);
}

export function signalSeverity(name: string, value: unknown): FraudSeverity {
  if (value === null || value === undefined || !isFraudSignalKey(name)) return 'unknown';
  return SIGNAL_SEVERITY[name][String(value)] || 'unknown';
}

export function meaningfulSignals(
  signals: object | null | undefined
): Array<{ key: FraudSignalKey; value: unknown; sev: FraudSeverity }> {
  if (!signals) return [];

  const ranked: FraudSeverity[] = ['critical', 'high', 'medium', 'low'];
  const meaningful: Array<{ key: FraudSignalKey; value: unknown; sev: FraudSeverity }> = [];

  Object.entries(signals).forEach(([key, value]) => {
    if (!isFraudSignalKey(key)) return;
    const sev = signalSeverity(key, value);
    if (ranked.includes(sev)) meaningful.push({ key, value, sev });
  });

  return meaningful.sort((a, b) => ranked.indexOf(a.sev) - ranked.indexOf(b.sev));
}
